/* eslint-disable react/no-array-index-key */
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { PRIME_COLOR_MAP } from '@propertypal/shared/src/constants/prime';
import bronzeRentPreview from '@propertypal/shared/src/resources/images/prime/bronze-rent.png';
import bronzeSalesPreview from '@propertypal/shared/src/resources/images/prime/bronze-sales.png';
import goldRentPreview from '@propertypal/shared/src/resources/images/prime/gold-rent.png';
import goldSalesPreview from '@propertypal/shared/src/resources/images/prime/gold-sales.png';
import showcaseEmailRent from '@propertypal/shared/src/resources/images/prime/showcase-email-rent.jpg';
import showcaseEmail from '@propertypal/shared/src/resources/images/prime/showcase-email.webp';
import silverRentPreview from '@propertypal/shared/src/resources/images/prime/silver-rent.png';
import silverSalesPreview from '@propertypal/shared/src/resources/images/prime/silver-sales.png';
import { trackGaLink } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types';
import { ListingTier, PrimePricingResponse, PrimeProductResponse } from '@propertypal/shared/src/types/prime';
import { roundOff } from '@propertypal/shared/src/utils/number';
import platform from '@propertypal/shared/src/utils/platform';
import { titleCase } from '@propertypal/shared/src/utils/text';
import Link from 'next/link';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import FontAwesomeIcon from '../../icons/FontAwesomeIcon';
import WhiteModal from '../../modal/WhiteModal';
import { closeTooltip, openTooltip } from '../../notifications/Tooltip';
import { Text } from '../../typography';
import PrimeOverlay from './PrimeOverlay';
import { PrimeOverlayContainer } from './PrimeOverlay.style';
import {
  Container,
  ComparisonTable,
  DiscountBubble,
  FeatureCellContent,
  FeatureLabel,
  FeatureValue,
  HighlightCell,
  InfoButton,
  MobileFeatureRow,
  MobileTierCard,
  MobileTierHeader,
  MobileTierSwiper,
  MobileTierSwiperPrompt,
  PriceCurrent,
  PricePrevious,
  PriceWrap,
  RecommendedTag,
  Tab,
  TabBar,
  TableWrap,
  TierHeading,
  ViewExampleButton,
  PreviewImage,
  PreviewSwiperWrap,
  UpgradeButton,
  UpgradeCell,
  Ruler,
  BoxShadow,
  ColumnOverlay,
} from './PrimeTiers.style';

const TIERS = ['BRONZE', 'SILVER', 'GOLD'] as const;
type PrimeTier = (typeof TIERS)[number];

type BillingMode = 'sale' | 'rental';

const TIER_PREVIEW_IMAGES: Record<PrimeTier, Record<BillingMode, string>> = {
  BRONZE: { sale: bronzeSalesPreview.src, rental: bronzeRentPreview.src },
  SILVER: { sale: silverSalesPreview.src, rental: silverRentPreview.src },
  GOLD: { sale: goldSalesPreview.src, rental: goldRentPreview.src },
};

const EMAIL_ALERT_PREVIEW: Record<BillingMode, string> = {
  sale: showcaseEmail.src,
  rental: showcaseEmailRent.src,
};

const TIER_CTA: Record<PrimeTier, string> = {
  BRONZE: 'Get more visibility',
  SILVER: 'Get priority placement',
  GOLD: 'Get maximum exposure',
};

const FEATURE_ROWS: Array<{
  key: string;
  values: Record<'BRONZE' | 'SILVER' | 'GOLD', React.ReactNode>;
}> = [
  {
    key: 'Search position',
    values: {
      BRONZE: 'Above standard ads',
      SILVER: 'Priority over Bronze',
      GOLD: 'Top of every search',
    },
  },
  {
    key: 'Listing size',
    values: {
      BRONZE: '1.5x larger',
      SILVER: '1.5x larger',
      GOLD: '2x larger',
    },
  },
  {
    key: 'Performance stats',
    values: {
      BRONZE: 'Included',
      SILVER: 'Included',
      GOLD: 'Included',
    },
  },
  {
    key: 'Agent branding',
    values: {
      BRONZE: 'Included',
      SILVER: 'Included',
      GOLD: 'Extra agent branding',
    },
  },
  {
    key: 'Extra photos',
    values: {
      BRONZE: '',
      SILVER: '+2 photos',
      GOLD: '+4 photos',
    },
  },
  {
    key: 'Renter email alert',
    values: {
      BRONZE: '',
      SILVER: '',
      GOLD: <b>Sent to active renters in your area</b>,
    },
  },
];

const MOBILE_FEATURE_ROWS: Record<
  PrimeTier,
  Array<{
    text: string | React.ReactNode;
    disabled?: boolean;
    emailAlertPreview?: boolean;
    performanceTooltip?: boolean;
  }>
> = {
  BRONZE: [
    { text: 'Above Standard Ads' },
    { text: '1.5x large ad' },
    { text: 'Performance Stats', performanceTooltip: true },
    { text: 'Agent branding Included' },
    { text: 'Extra Photos', disabled: true },
    { text: 'Buyer email alert', disabled: true },
  ],
  SILVER: [
    { text: 'Priority over Bronze Ads' },
    { text: '1.5x larger ad' },
    { text: 'Performance Stats', performanceTooltip: true },
    { text: 'Agent branding included' },
    { text: '+2 photos' },
    { text: 'Buyer Email Alert', disabled: true },
  ],
  GOLD: [
    { text: 'Top of every search' },
    { text: '2x larger ad' },
    { text: 'Performance stats', performanceTooltip: true },
    { text: 'Extra agent branding' },
    { text: '+4 photos' },
    { emailAlertPreview: true, text: <b>Email alert sent to active buyers in your area</b> },
  ],
};

interface PrimeTiersProps {
  property?: Property | null;
  products?: PrimeProductResponse[] | null;
  daysRemaining?: number | null;
  current?: ListingTier | null;
  agent?: boolean;
  primePricing: PrimePricingResponse;
}

const getTierPricing = (
  tier: PrimeTier,
  primePricing: PrimePricingResponse,
  products: PrimeProductResponse[] | null | undefined,
) => {
  const pricingProduct = primePricing?.products.find((p) => p.listingTier === tier);

  if (pricingProduct) {
    const price = pricingProduct.sale.preIntroPrice ?? pricingProduct.sale.price;
    const discountPrice = pricingProduct.sale.preIntroPrice ? pricingProduct.sale.price : undefined;
    const rentalPrice = pricingProduct.rental.preIntroPrice ?? pricingProduct.rental.price;
    const discountRentalPrice = pricingProduct.rental.preIntroPrice ? pricingProduct.rental.price : undefined;
    const discount = discountPrice ? roundOff(((price - discountPrice) / price) * 100, 0) : undefined;
    return { price, discountPrice, rentalPrice, discountRentalPrice, discount };
  }

  // Fallback: property-specific upgrade pricing (BRONZE/SILVER property page)
  const upgradeProduct = products?.find((p) => p.listingTier === tier);
  if (upgradeProduct) {
    const hasDiscount =
      upgradeProduct.effectivePrice !== null && upgradeProduct.effectivePrice !== upgradeProduct.standardPrice;
    const discountPrice = hasDiscount ? (upgradeProduct.effectivePrice as number) : undefined;
    const discount = discountPrice
      ? roundOff(((upgradeProduct.standardPrice - discountPrice) / upgradeProduct.standardPrice) * 100, 0)
      : undefined;
    return {
      price: upgradeProduct.standardPrice,
      discountPrice,
      rentalPrice: upgradeProduct.standardPrice,
      discountRentalPrice: discountPrice,
      discount,
    };
  }

  return { price: 0, discountPrice: undefined, rentalPrice: 0, discountRentalPrice: undefined, discount: undefined };
};

const buildPriceCell = (
  tier: PrimeTier,
  mode: BillingMode,
  pricing: ReturnType<typeof getTierPricing>,
  isUpgrade: boolean,
  testIdSuffix = '',
) => {
  const fullPrice = mode === 'sale' ? pricing.price : pricing.rentalPrice;
  const offerPrice = mode === 'sale' ? pricing.discountPrice : pricing.discountRentalPrice;

  if (offerPrice) {
    return (
      <PriceWrap data-testid={`tier-price-${mode}-${tier}${testIdSuffix}`}>
        <PricePrevious>Price &pound;{fullPrice}</PricePrevious>
        <PriceCurrent>
          <span>{isUpgrade ? 'Upgrade Offer: ' : 'Intro Offer: '}</span>&pound;{offerPrice}
        </PriceCurrent>
      </PriceWrap>
    );
  }

  return (
    <PriceWrap data-testid={`tier-price-${mode}-${tier}${testIdSuffix}`}>
      <PricePrevious style={{ textDecoration: 'none' }}>&nbsp;</PricePrevious>
      <PriceCurrent>&pound;{fullPrice}</PriceCurrent>
    </PriceWrap>
  );
};

const getDiscountForMode = (mode: BillingMode, pricing: ReturnType<typeof getTierPricing>) => {
  const fullPrice = mode === 'sale' ? pricing.price : pricing.rentalPrice;
  const offerPrice = mode === 'sale' ? pricing.discountPrice : pricing.discountRentalPrice;

  if (!fullPrice || !offerPrice || offerPrice >= fullPrice) return undefined;

  return roundOff(((fullPrice - offerPrice) / fullPrice) * 100, 0);
};

const PrimeTiers: React.FC<PrimeTiersProps> = ({ property, products, daysRemaining, current, agent, primePricing }) => {
  const [mode, setMode] = useState<BillingMode>(property?.saleType?.key === 'rent' ? 'rental' : 'sale');
  const [selectedPreviewTier, setSelectedPreviewTier] = useState<PrimeTier | null>(null);
  const [activePreviewTier, setActivePreviewTier] = useState<PrimeTier | null>(null);
  const [showEmailAlertPreview, setShowEmailAlertPreview] = useState(false);
  const emptyRect = { left: 0, width: 0, top: 0, height: 0, visible: false };
  const [columnRects, setColumnRects] = useState<Record<PrimeTier, typeof emptyRect>>({
    BRONZE: emptyRect,
    SILVER: emptyRect,
    GOLD: emptyRect,
  });
  const tableRef = useRef<HTMLTableElement | null>(null);
  const headerRefs = useRef<Record<PrimeTier, HTMLTableCellElement | null>>({
    BRONZE: null,
    SILVER: null,
    GOLD: null,
  });
  const theme = useTheme();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as Node;
      const tooltipElement = document.getElementById('tooltip');
      const infoButtons = document.getElementsByClassName('performance-tooltip');

      const clickedInsideButton = Array.from(infoButtons).some((button) => button.contains(clickedElement));
      const clickedInsideTooltip = tooltipElement?.contains(clickedElement);
      if (!clickedInsideButton && !clickedInsideTooltip) {
        closeTooltip();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const updateShadow = () => {
      const table = tableRef.current;

      if (!table) {
        setColumnRects((prev) =>
          TIERS.reduce(
            (acc, tier) => ({ ...acc, [tier]: { ...prev[tier], visible: false } }),
            {} as Record<PrimeTier, typeof emptyRect>,
          ),
        );
        return;
      }

      setColumnRects((prev) =>
        TIERS.reduce(
          (acc, tier) => {
            const header = headerRefs.current[tier];

            acc[tier] = header
              ? {
                  left: header.offsetLeft,
                  width: header.offsetWidth,
                  top: table.offsetTop,
                  height: table.offsetHeight,
                  visible: true,
                }
              : { ...prev[tier], visible: false };

            return acc;
          },
          {} as Record<PrimeTier, typeof emptyRect>,
        ),
      );
    };

    updateShadow();

    const resizeObserver = new ResizeObserver(updateShadow);
    if (tableRef.current) resizeObserver.observe(tableRef.current);
    TIERS.forEach((tier) => {
      const header = headerRefs.current[tier];
      if (header) resizeObserver.observe(header);
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const isUpgrade = daysRemaining != null;
  const tierData = useMemo(() => {
    return TIERS.map((tier) => {
      const upgradeProduct = products?.find((p) => p.listingTier === tier);
      const pricing = getTierPricing(tier, primePricing, products);

      return {
        tier,
        pricing,
        isCurrent: tier === current,
        alreadyUpgraded: tier === 'BRONZE' && current === 'SILVER',
        eligibleForPurchase: !upgradeProduct ? true : upgradeProduct.eligibleForPurchase,
      };
    });
  }, [products, primePricing, current]);

  const getFeatureValue = (row: (typeof FEATURE_ROWS)[number], tier: PrimeTier) => {
    if (row.key === 'Renter email alert' && tier === 'GOLD') {
      return mode === 'sale' ? <b>Sent to active buyers in your area</b> : <b>Sent to active renters in your area</b>;
    }

    return row.values[tier];
  };

  const renderPerformanceTooltipButton = (testIdSuffix = '') => (
    <InfoButton
      className="performance-tooltip"
      onClick={(e) => {
        openTooltip(
          <span>
            Prime listings unlock additional insights like views, favourites and alert appearances on your property.{' '}
            {agent ? (
              `Your vendor will need a ${platform.title} account to view these stats.`
            ) : (
              <>
                You need to have a {platform.title} account to view these stats. Make sure to{' '}
                <Link
                  href="/register"
                  prefetch={false}
                  style={{ textDecoration: 'underline' }}
                  onClick={() => closeTooltip()}
                >
                  sign up
                </Link>
                !
              </>
            )}
          </span>,
          e,
          theme.textDark,
          true,
        );
      }}
      data-testid={`performance-tooltip${testIdSuffix}`}
      type="button"
    >
      <FontAwesomeIcon icon={faInfoCircle} color={theme.textDark} size={14} />
    </InfoButton>
  );

  const renderFeatureLabel = (rowKey: string, testIdSuffix = '') => (
    <>
      {rowKey === 'Renter email alert' ? (mode === 'sale' ? 'Buyer Email Alert' : 'Renter Email Alert') : rowKey}
      {rowKey === 'Performance stats' && renderPerformanceTooltipButton(testIdSuffix)}
    </>
  );

  return (
    <Container data-testid="prime-tiers">
      {!property && (
        <TabBar role="tablist" aria-label="Prime pricing mode">
          <Tab
            type="button"
            role="tab"
            aria-selected={mode === 'sale'}
            onClick={() => setMode('sale')}
            data-testid="prime-tab-sales"
          >
            Sales
          </Tab>
          <Tab
            type="button"
            role="tab"
            aria-selected={mode === 'rental'}
            onClick={() => setMode('rental')}
            data-testid="prime-tab-rentals"
          >
            Rentals
          </Tab>
        </TabBar>
      )}

      <TableWrap>
        <BoxShadow
          style={{
            left: columnRects.SILVER.left,
            width: columnRects.SILVER.width,
            top: columnRects.SILVER.top,
            height: columnRects.SILVER.height,
            opacity: columnRects.SILVER.visible ? 1 : 0,
          }}
        />
        <ComparisonTable ref={tableRef}>
          <thead>
            <tr>
              <FeatureLabel style={{ color: theme.backgroundMid }}>COMPARE FEATURES</FeatureLabel>
              {tierData.map((tier) => {
                const color = PRIME_COLOR_MAP[tier.tier];
                const discount = getDiscountForMode(mode, tier.pricing);

                return (
                  <HighlightCell
                    key={`header-${tier.tier}`}
                    ref={(el) => {
                      headerRefs.current[tier.tier] = el;
                    }}
                    $highlight={tier.tier === 'SILVER'}
                  >
                    {discount ? (
                      <DiscountBubble style={{ backgroundColor: color }}>
                        {discount}%<span>OFF</span>
                      </DiscountBubble>
                    ) : null}
                    {tier.tier === 'SILVER' && !(property && tier.isCurrent) && (
                      <RecommendedTag>Recommended</RecommendedTag>
                    )}
                    <TierHeading style={{ color }}>{titleCase(tier.tier)}</TierHeading>
                    <ViewExampleButton
                      style={{ color }}
                      type="button"
                      onClick={() => {
                        setSelectedPreviewTier(tier.tier);
                        setActivePreviewTier(tier.tier);
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} size={14} />
                      View example listing
                    </ViewExampleButton>
                    {tier.eligibleForPurchase && buildPriceCell(tier.tier, mode, tier.pricing, isUpgrade)}
                    <Ruler style={{ backgroundColor: color }} />
                  </HighlightCell>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {FEATURE_ROWS.map((row) => (
              <tr key={row.key}>
                <FeatureLabel $border>
                  {renderFeatureLabel(row.key)}
                  {row.key === 'Renter email alert' && (
                    <InfoButton
                      aria-label="View email alert preview"
                      data-testid="email-alert-preview-button"
                      onClick={() => setShowEmailAlertPreview(true)}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faInfoCircle} color={theme.textDark} size={14} />
                    </InfoButton>
                  )}
                </FeatureLabel>

                {tierData.map((tier) => (
                  <FeatureValue key={`${row.key}-${tier.tier}`} $highlight={tier.tier === 'SILVER'}>
                    {(() => {
                      const value = getFeatureValue(row, tier.tier);

                      return (
                        <FeatureCellContent>
                          <FontAwesomeIcon
                            icon={!value ? faXmark : faCheck}
                            color={!value ? '#9fa6b7' : '#2f9e44'}
                            size={14}
                          />
                          {value}
                        </FeatureCellContent>
                      );
                    })()}
                  </FeatureValue>
                ))}
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <FeatureLabel>&nbsp;</FeatureLabel>
              {tierData.map((tier) => {
                const colour = PRIME_COLOR_MAP[tier.tier];
                const ctaText = TIER_CTA[tier.tier];

                return (
                  <UpgradeCell key={`upgrade-${tier.tier}`} $highlight={tier.tier === 'SILVER'}>
                    <UpgradeButton
                      href={`${platform.ordersUrl}/prime${
                        property?.id ? `/${property.id}` : ''
                      }?tier=${tier.tier.toLowerCase()}`}
                      onClick={trackGaLink(
                        'upgrade_click',
                        `Prime - ${titleCase(tier.tier)}`,
                        agent ? 'Agent Prime Landing Page' : 'User Prime Landing Page',
                      )}
                      $bg={colour}
                      $filled={tier.tier === 'SILVER'}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`upgrade-${tier.tier}-button`}
                    >
                      {tier.eligibleForPurchase ? ctaText : 'Unavailable'}
                    </UpgradeButton>
                  </UpgradeCell>
                );
              })}
            </tr>
          </tfoot>
        </ComparisonTable>

        {tierData.map((tier) => {
          if (!tier.alreadyUpgraded && !(tier.isCurrent && tier.tier !== 'GOLD')) return null;

          const rect = columnRects[tier.tier];
          if (!rect.visible) return null;

          return (
            <ColumnOverlay
              key={`overlay-${tier.tier}`}
              style={{ left: rect.left, width: rect.width, top: rect.top, height: rect.height }}
            >
              {tier.alreadyUpgraded && <PrimeOverlayContainer $tier />}
              {tier.isCurrent && tier.tier !== 'GOLD' && (
                <PrimeOverlay current={tier.tier} tier={tier.tier} currentPrimeItem={property?.currentPrimeItem} />
              )}
            </ColumnOverlay>
          );
        })}
      </TableWrap>

      <MobileTierSwiper>
        <MobileTierSwiperPrompt>
          <FontAwesomeIcon icon={faArrowLeft} size={13} />
          <span>swipe to compare</span>
          <FontAwesomeIcon icon={faArrowRight} size={13} />
        </MobileTierSwiperPrompt>

        <Swiper
          centeredSlides
          initialSlide={1}
          slidesPerView={1.15}
          spaceBetween={16}
          onSlideChange={() => closeTooltip()}
        >
          {tierData.map((tier) => {
            const color = PRIME_COLOR_MAP[tier.tier];
            const discount = getDiscountForMode(mode, tier.pricing);
            const ctaText = TIER_CTA[tier.tier];

            return (
              <SwiperSlide key={`mobile-${tier.tier}`}>
                <MobileTierCard $highlight={tier.tier === 'SILVER'}>
                  <MobileTierHeader>
                    {discount ? (
                      <DiscountBubble style={{ backgroundColor: color, right: -10 }}>
                        {discount}%<span>OFF</span>
                      </DiscountBubble>
                    ) : null}
                    {tier.tier === 'SILVER' && !(property && tier.isCurrent) && (
                      <RecommendedTag>Recommended</RecommendedTag>
                    )}
                    {tier.alreadyUpgraded && <PrimeOverlayContainer $tier />}
                    {tier.isCurrent && tier.tier !== 'GOLD' && (
                      <PrimeOverlay
                        current={tier.tier}
                        tier={tier.tier}
                        currentPrimeItem={property?.currentPrimeItem}
                      />
                    )}
                    <TierHeading style={{ color }}>{titleCase(tier.tier)}</TierHeading>
                    <ViewExampleButton
                      style={{ color }}
                      type="button"
                      onClick={() => {
                        setSelectedPreviewTier(tier.tier);
                        setActivePreviewTier(tier.tier);
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} size={14} />
                      View example listing
                    </ViewExampleButton>
                    {tier.eligibleForPurchase && buildPriceCell(tier.tier, mode, tier.pricing, isUpgrade, '-mobile')}
                    <Ruler style={{ backgroundColor: color }} />
                  </MobileTierHeader>

                  {MOBILE_FEATURE_ROWS[tier.tier].map((feature) => (
                    <MobileFeatureRow key={`${feature.text}-${tier.tier}-mobile`} $disabled={feature.disabled}>
                      <FontAwesomeIcon
                        icon={feature.disabled ? faXmark : faCheck}
                        color={feature.disabled ? '#9fa6b7' : '#2f9e44'}
                        size={14}
                      />
                      <p>
                        {feature.text}
                        {feature.performanceTooltip &&
                          renderPerformanceTooltipButton(`-${tier.tier.toLowerCase()}-mobile`)}
                        {feature.emailAlertPreview && (
                          <InfoButton
                            aria-label="View email alert preview"
                            data-testid="email-alert-preview-button-mobile"
                            onClick={() => setShowEmailAlertPreview(true)}
                            type="button"
                          >
                            <FontAwesomeIcon icon={faInfoCircle} color={theme.textDark} size={14} />
                          </InfoButton>
                        )}
                      </p>
                    </MobileFeatureRow>
                  ))}

                  <UpgradeButton
                    href={`${platform.ordersUrl}/prime${
                      property?.id ? `/${property.id}` : ''
                    }?tier=${tier.tier.toLowerCase()}`}
                    onClick={trackGaLink(
                      'upgrade_click',
                      `Prime - ${titleCase(tier.tier)}`,
                      agent ? 'Agent Prime Landing Page' : 'User Prime Landing Page',
                    )}
                    $bg={color}
                    $filled={tier.tier === 'SILVER'}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`upgrade-${tier.tier}-button-mobile`}
                  >
                    {tier.eligibleForPurchase ? ctaText : 'Unavailable'}
                  </UpgradeButton>
                </MobileTierCard>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </MobileTierSwiper>

      <WhiteModal
        show={!!selectedPreviewTier}
        onClose={() => setSelectedPreviewTier(null)}
        maxWidth={960}
        collapseContent
        title={activePreviewTier ? `${titleCase(activePreviewTier)} Example Listing` : 'Example Prime Listing'}
      >
        <MobileTierSwiperPrompt style={{ marginTop: 30 }}>
          <FontAwesomeIcon icon={faArrowLeft} size={13} />
          <span>swipe to compare</span>
          <FontAwesomeIcon icon={faArrowRight} size={13} />
        </MobileTierSwiperPrompt>

        {selectedPreviewTier && (
          <PreviewSwiperWrap>
            <Swiper
              key={selectedPreviewTier}
              modules={[Navigation]}
              navigation
              initialSlide={TIERS.indexOf(selectedPreviewTier)}
              onSlideChange={(swiper) => setActivePreviewTier(TIERS[swiper.activeIndex])}
            >
              {TIERS.map((tier) => (
                <SwiperSlide key={tier}>
                  <PreviewImage src={TIER_PREVIEW_IMAGES[tier][mode]} alt="Listing preview" />
                </SwiperSlide>
              ))}
            </Swiper>
          </PreviewSwiperWrap>
        )}
      </WhiteModal>

      <WhiteModal
        show={showEmailAlertPreview}
        onClose={() => setShowEmailAlertPreview(false)}
        maxWidth={600}
        title="Email Alert Preview"
      >
        <img src={EMAIL_ALERT_PREVIEW[mode]} alt="Email alert preview" style={{ width: '100%', height: 'auto' }} />
      </WhiteModal>

      <Text fontSize={12} align="center" style={{ maxWidth: 800, margin: '50px auto' }}>
        Your Prime advert will appear in the default search results for location-based searches for up to 90 days for
        sales properties and 45 days for rentals from the date of upgrade. Previous enhanced listings have received up
        to 80% more enquiries than standard listings*
        <br />
        *Based on historic performance of previous enhanced formats. Past performance is indicative only and not
        guaranteed. Results may vary.
      </Text>
    </Container>
  );
};

export default PrimeTiers;
